import { openPopup } from '@neb/popup';
import confetti from 'canvas-confetti';
import { LitElement, html, css } from 'lit';

import {
  updateTwoFactorAuth,
  getTwoFactorAuthStatus,
} from '../../../../neb-api-client/src/two-factor-auth-api-client';
import {
  openSuccess,
  openError,
} from '../../../../neb-dialog/neb-banner-state';
import { POPUP_RENDER_KEYS } from '../../../../neb-popup/src/renderer-keys';
import { store } from '../../../../neb-redux/neb-redux-store';
import {
  CSS_SPACING,
  CSS_COLOR_GREY_2,
  CSS_COLOR_HIGHLIGHT,
} from '../../../../neb-styles/neb-variables';
import {
  disableMfa,
  setupCognitoPhoneNumber,
  setupTOTP,
  verifyCognitoPhoneNumber,
  verifyTOTP,
} from '../../../../neb-utils/cognito-util';
import {
  hasFeatureOrBetaSync,
  FEATURE_FLAGS,
} from '../../../../neb-utils/feature-util';
import { formatToE164 } from '../../../../neb-utils/formatters';
import { phone } from '../../../../neb-utils/masks';
import { BUTTON_ROLE } from '../neb-button';

import { generateTOTPQRCode } from './qr-code-generator';
import '../inputs/neb-textfield';
import '../controls/neb-switch';
import '../neb-radio-button';

import '../../../../../src/components/misc/neb-card-switch-expanded';

import './neb-two-factor-auth';

import '../../../../../src/components/misc/neb-icon';

export const ELEMENTS = {
  container: { id: 'container' },
  containerSecurity: { id: 'container-security' },
  info: { id: 'info' },
  twoStepVerification: { id: 'two-step-verification' },
  twoStepMethodPhone: { id: 'two-step-method-phone' },
  twoStepMethodAuthenticator: { id: 'two-step-method-authenticator' },
  twoStepMethodsContainer: { id: 'two-step-methods-container' },
  phoneNumberInput: { id: 'phone-number-input' },
  sendCodeButton: { id: 'send-code-button' },
  verificationCodeInputs: { id: 'verification-code-inputs' },
  verification: { id: 'verification' },
  greenShieldCheckmarkIcon: { id: 'green-shield-checkbox-icon' },
};

const TWO_FACTOR_METHODS = [
  { value: 'phone', label: 'Phone' },
  { value: 'authenticator', label: 'Authenticator App' },
];

export const CONFIRM_DISABLE_2FA = {
  confirmText: 'Yes',
  cancelText: 'No',
  title: 'Disable Two-Step Verification',
  message: html` Are you sure you want to disable two-factor verification? `,
};

export default class NebSecurity extends LitElement {
  static get properties() {
    return {
      __setupStep: Number,
      model: Object,
      errors: Object,
      sendCode: String,
      layout: {
        type: String,
        reflect: true,
      },
      __sendCodeDisabled: { type: Boolean },
      __authenticatorSecretKey: String,
      __authenticatorQrCodeUrl: String,
      __authenticatorSetupComplete: { type: Boolean },
      __email: String,
      __refreshToken: Object,
      __isSettingUp2FA: { type: Boolean },
      __userEnteredPhoneNumber: String,
      __showSecretKey: { type: Boolean },
      __hasOwlHide2faSmsFeatureFlag: { type: Boolean },
    };
  }

  constructor() {
    super();
    this.__initState();
    this.__initHandlers();
  }

  connectedCallback() {
    super.connectedCallback();
    const state = store.getState();
    const {
      session: {
        item: {
          idToken: {
            payload: { email },
          },
          refreshToken,
          id: cognitoId,
        },
      },
    } = state;

    this.__email = email;
    this.__refreshToken = refreshToken;

    this.__hasOwlHide2faSmsFeatureFlag = hasFeatureOrBetaSync(
      FEATURE_FLAGS.OWL_HIDE_2FA_SMS,
    );

    if (cognitoId) {
      getTwoFactorAuthStatus(cognitoId)
        .then(status => {
          let formattedPhoneNumber = '';

          const phoneNumber = status.twoStepPhoneNumber;

          if (phoneNumber) {
            if (phoneNumber.startsWith('+1')) {
              const rawNumber = phoneNumber.substring(2);
              formattedPhoneNumber = phone.toFormattedValue(rawNumber);
            } else {
              formattedPhoneNumber = phoneNumber;
            }
            this.__userEnteredPhoneNumber = formattedPhoneNumber;
          }

          const defaultMethod = this.__hasOwlHide2faSmsFeatureFlag
            ? 'authenticator'
            : 'phone';

          this.model = {
            ...this.model,
            twoStepVerificationEnabled: status.twoStepVerificationEnabled,
            twoStepMethod: status.twoStepMethod || defaultMethod,
            twoStepPhoneNumber: formattedPhoneNumber,
          };

          if (status.twoStepVerificationEnabled) {
            this.__setupStep = 3;
          }

          this.requestUpdate();
        })
        .catch(() => {
          store.dispatch(
            openError('Failed to load two-factor authentication settings.'),
          );
        });
    }
  }

  __initState() {
    this.__setupStep = 1;
    this.layout = '';
    this.model = NebSecurity.createModel();
    this.errors = {};
    this.__sendCodeDisabled = true;
    this.__authenticatorSecretKey = '';
    this.__authenticatorQrCodeUrl = '';
    this.__authenticatorSetupComplete = false;
    this.email = '';
    this.refreshToken = {};
    this.__isSettingUp2FA = false;
    this.__userEnteredPhoneNumber = '';
    this.__showSecretKey = false;

    this.onChange = () => {};

    this.onSave = () => {};

    this.onChangeDirty = () => {};
  }

  static createModel() {
    const hasOwlHide2faSmsFeatureFlag = hasFeatureOrBetaSync(
      FEATURE_FLAGS.OWL_HIDE_2FA_SMS,
    );

    const defaultMethod = hasOwlHide2faSmsFeatureFlag
      ? 'authenticator'
      : 'phone';

    return {
      twoStepVerificationEnabled: false,
      twoStepMethod: defaultMethod,
      twoStepPhoneNumber: '',
    };
  }

  static createSelectors() {
    return {
      twoStepVerificationEnabled: {
        validators: [],
        defaultValue: false,
      },
      twoStepMethod: {
        validators: [],
        defaultValue: 'phone',
        options: TWO_FACTOR_METHODS.map(method => ({
          value: method.value,
          label: method.label,
        })),
      },
      twoStepPhoneNumber: {
        validators: [],
        defaultValue: '',
      },
    };
  }

  __initHandlers() {
    this.__handlers = {
      change: async e => {
        const event = e.detail || e;
        const { session } = store.getState();
        const cognitoId = session?.item?.id;

        if (event.name === 'showTwoStepSetup') {
          await this.__handleTwoStepSetupChange(event.value, cognitoId);
        } else if (event.name === 'twoStepPhoneNumber') {
          this.__handlePhoneNumberChange(event);
        } else {
          this.__handleModelChange(event);
        }

        this.requestUpdate();
      },

      sendCode: async () => {
        if (this.model.twoStepPhoneNumber) {
          this.__sendCodeDisabled = true;

          await setupCognitoPhoneNumber(
            this.__email,
            this.__refreshToken,
            this.model.twoStepPhoneNumber,
          );

          this.__setupStep = 2;
          this.requestUpdate();
        }
      },
    };
  }

  static get styles() {
    return [
      css`
        .container {
          flex: 1 0 0;
          display: block;
          height: fit-content;
          width: 100%;
          padding: ${CSS_SPACING} 0;
          max-width: 900px;
          margin: 0 auto;
        }
        .info {
          margin-bottom: ${CSS_SPACING};
          padding: 0 ${CSS_SPACING};
        }
        .seperator {
          display: flex;
          width: 100%;
          margin-bottom: ${CSS_SPACING};
          box-sizing: border-box;
          border: 1px solid ${CSS_COLOR_GREY_2};
          border-image: initial;
        }
        .permission {
          display: flex;
          margin-bottom: ${CSS_SPACING};
          padding: 0 ${CSS_SPACING};
        }
        :host([layout='small']) .permission {
          flex-direction: column;
        }
        :host([layout='small'])
          neb-card-switch-expanded::part(title-container) {
          flex-direction: column;
          align-items: flex-start;
          gap: 8px;
        }
        :host([layout='small']) neb-card-switch-expanded::part(description) {
          padding-left: 0;
        }
        .method-label {
          font-weight: 500;
          margin-top: ${CSS_SPACING};
          margin-bottom: 10px;
          display: block;
        }
        .setup-step {
          margin-top: 20px;
          padding-top: 20px;
        }
        .step-heading {
          font-size: 16px;
          font-weight: 600;
          margin: 50px 0 8px 0;
        }
        .step-heading:first-of-type {
          margin-top: 24px;
        }
        .step-description {
          margin: 0 0 24px 0;
        }
        .phone-input-container {
          margin-bottom: 20px;
          width: 450px;
          display: flex;
          align-items: center;
          gap: 16px;
        }
        :host([layout='small']) .phone-input-container {
          width: 100%;
          max-width: 450px;
        }
        .send-code-button::part(button) {
          height: 35px;
          border-radius: 5px;
          margin-top: 15px;
        }
        :host([layout='small']) .send-code-button::part(button) {
          width: 100%;
          white-space: nowrap;
        }
        .code-verification-container {
          margin-top: 20px;
          max-width: 300px;
        }
        .code-inputs {
          display: flex;
          justify-content: space-between;
          margin-top: 10px;
        }
        .code-input {
          width: 40px;
          height: 40px;
          font-size: 20px;
          text-align: center;
          border: 1px solid ${CSS_COLOR_GREY_2};
          border-radius: 4px;
          margin-right: 5px;
        }
        .code-input:focus {
          border-color: ${CSS_COLOR_HIGHLIGHT};
          outline: none;
        }
        .radio-options {
          display: grid;
          grid-template-columns: 90px 15px 150px 175px 15px 100px;
          margin-top: 10px;
          align-items: center;
          gap: 0 16px;
        }
        .radio-group {
          display: contents;
        }
        .verified-text {
          background-color: #4caf50;
          color: white;
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 14px;
          white-space: nowrap;
          justify-self: start;
        }
        :host([layout='small']) .radio-options {
          grid-template-columns: 1fr;
          gap: 0 16px;
        }
        :host([layout='small']) .radio-group {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        :host([layout='small']) neb-radio-button:first-of-type,
        :host([layout='small']) neb-radio-button:last-of-type {
          grid-column: auto;
          justify-self: auto;
        }
        :host([layout='small']) neb-icon {
          justify-self: auto;
        }
        :host([layout='small']) .verified-text {
          justify-self: auto;
        }
        .celebration-title {
          font-weight: bold;
        }
        .celebration-icons {
          display: flex;
          justify-content: center;
          margin-bottom: 8px;
          padding-top: 16px;
        }
        .celebration-icon {
          font-size: 64px;
          height: 64px;
          width: 64px;
          animation: bounce 1s ease-in-out infinite;
          filter: drop-shadow(0 0 8px rgba(76, 175, 80, 0.6));
        }
        :host([layout='small']) .celebration-icon {
          height: 40px;
          width: 50px;
        }
        :host([layout='small']) .celebration-message {
          padding: 16px;
          font-size: 15px;
          margin: 20px;
        }
        .celebration-message {
          text-align: center;
          margin: 32px auto;
          padding: 32px;
          max-width: 600px;
          background: linear-gradient(45deg, #4caf50, #1976d2);
          color: white;
          border-radius: 12px;
          font-size: 18px;
          font-weight: 500;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          animation:
            slideIn 0.5s ease-out,
            glow 2s ease-in-out infinite;
          display: flex;
          flex-direction: column;
          gap: 16px;
          line-height: 1.5;
          word-break: break-word;
        }
        @keyframes slideIn {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes glow {
          0% {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }
          50% {
            box-shadow: 0 4px 24px rgba(76, 175, 80, 0.3);
          }
          100% {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }
        }
        @keyframes bounce {
          0%,
          100% {
            transform: translateY(0);
            filter: drop-shadow(0 0 8px rgba(76, 175, 80, 0.6));
          }
          50% {
            transform: translateY(-10px);
            filter: drop-shadow(0 0 16px rgba(76, 175, 80, 0.8));
          }
        }
        .qr-code-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin: 16px 0 8px 0;
        }
        .qr-code {
          background: white;
          padding: 16px;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        .secret-key-container {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          margin-top: 8px;
        }
        .secret-key {
          font-family: monospace;
          font-size: 16px;
          font-weight: bold;
          letter-spacing: 2px;
          color: #333;
          padding: 16px;
          background-color: #f5f5f5;
          border-radius: 8px;
          word-break: break-all;
          flex: 1;
          text-align: center;
        }
        .copy-button {
          padding: 8px;
          border: none;
          background: transparent;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: background-color 0.2s;
        }
        .copy-button:hover {
          background-color: rgba(0, 0, 0, 0.05);
        }
        .copy-button neb-icon {
          width: 20px;
          height: 20px;
          color: #666;
        }
        .secret-key-link {
          color: #00a4e4;
          cursor: pointer;
          text-decoration: underline;
          margin: 24px 0;
          display: inline-block;
        }
        .verification-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
          width: 100%;
          max-width: 320px;
          align-self: center;
          margin-top: 8px;
        }
        .verification-code-input {
          width: 100%;
        }
        .verify-button {
          width: 100%;
        }
        .success-icon {
          color: var(--neb-success);
          margin-left: 8px;
        }
        .verified-badge-flex {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .radio-options--auth-only {
          display: grid;
          grid-template-columns: 1fr;
          align-items: center;
        }
        .radio-group--auth-only {
          display: flex;
          align-items: center;
          gap: 12px;
        }
      `,
    ];
  }

  __updateParentFormModel() {
    const event = new CustomEvent('security-update', {
      detail: {
        twoStepVerificationEnabled: this.model.twoStepVerificationEnabled,
        twoStepPhoneNumber: this.model.twoStepPhoneNumber,
        twoStepMethod: this.model.twoStepMethod,
        skipDismiss: true,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);

    const baseUpdate = {
      skipDismiss: true,
    };

    this.onChange({
      name: 'twoStepVerificationEnabled',
      value: this.model.twoStepVerificationEnabled,
      ...baseUpdate,
    });

    this.onChange({
      name: 'twoStepPhoneNumber',
      value: this.model.twoStepPhoneNumber,
      ...baseUpdate,
    });

    this.onChange({
      name: 'twoStepMethod',
      value: this.model.twoStepMethod,
      ...baseUpdate,
    });
  }

  async __handleVerificationCodeChange(code) {
    if (code.length !== 6) return;

    try {
      await verifyCognitoPhoneNumber(this.__email, this.__refreshToken, code);

      this.onChange({
        name: 'twoStepVerificationEnabled',
        value: true,
        skipDismiss: true,
      });

      this.onChange({
        name: 'twoStepMethod',
        value: 'phone',
        skipDismiss: true,
      });

      const e164PhoneNumber = formatToE164(this.model.twoStepPhoneNumber);
      const cognitoId = store.getState().session?.item?.id;

      await updateTwoFactorAuth(cognitoId, {
        twoStepVerificationEnabled: true,
        twoStepMethod: this.model.twoStepMethod,
        twoStepPhoneNumber: e164PhoneNumber,
      });

      const refreshedStatus = await getTwoFactorAuthStatus(cognitoId);

      let formattedPhoneNumber = '';

      const refreshedPhoneNumber = refreshedStatus.twoStepPhoneNumber;

      if (refreshedPhoneNumber) {
        if (refreshedPhoneNumber.startsWith('+1')) {
          const rawNumber = refreshedPhoneNumber.substring(2);
          formattedPhoneNumber = phone.toFormattedValue(rawNumber);
        } else {
          formattedPhoneNumber = refreshedPhoneNumber;
        }
        this.__userEnteredPhoneNumber = formattedPhoneNumber;
      }
      this.model = {
        ...this.model,
        ...refreshedStatus,
        verified: true,
        twoStepPhoneNumber: formattedPhoneNumber,
        skipDismiss: true,
      };

      store.dispatch(openSuccess('Your phone number is verified'));
      const event = new CustomEvent('security-update', {
        detail: {
          ...this.model,
          skipDismiss: true,
        },
        bubbles: true,
        composed: true,
      });
      this.dispatchEvent(event);

      this.__updateParentFormModel();

      const saveEvent = new CustomEvent('security-save', {
        detail: {
          ...this.model,
          skipDismiss: true,
        },
        bubbles: true,
        composed: true,
      });
      this.dispatchEvent(saveEvent);

      await this.__triggerConfetti();
      await this.onSave({
        ...this.model,
        skipDismiss: true,
      });

      this.onChangeDirty(false);
    } catch (error) {
      this.__handleVerificationError();
      store.dispatch(
        openError('Failed to load two-factor authentication settings.'),
      );

      throw error;
    }
  }

  __handleVerificationError() {
    const verificationComponent = this.shadowRoot.querySelector(
      'neb-two-factor-auth',
    );

    if (verificationComponent) {
      verificationComponent.__error = true;
    }

    store.dispatch(openError('Invalid verification code. Please try again.'));
  }

  __triggerConfetti() {
    const shieldIcon = this.shadowRoot.querySelector('.celebration-icon');

    if (shieldIcon) {
      const rect = shieldIcon.getBoundingClientRect();
      const iconCenterX = rect.left + rect.width / 2;
      const iconCenterY = rect.top + rect.height / 2;
      const originX = iconCenterX / window.innerWidth;
      const originY = iconCenterY / window.innerHeight;

      confetti({
        particleCount: 200,
        spread: 120,
        origin: { x: originX, y: originY },
        colors: ['#4caf50', '#1976d2', '#ffd700'],
        gravity: 0.7,
        scalar: 1.2,
        ticks: 300,
        decay: 0.95,
        startVelocity: 35,
      });

      setTimeout(() => {
        const canvases = document.querySelectorAll('body > canvas');
        canvases.forEach(canvas => {
          if (canvas.style.position === 'fixed') {
            canvas.style.zIndex = '9999';
            canvas.style.pointerEvents = 'none';
          }
        });
      }, 0);
    }
  }

  __toggleSecretKey() {
    this.__showSecretKey = !this.__showSecretKey;
    this.requestUpdate();
  }

  async __copySecretKey() {
    try {
      await navigator.clipboard.writeText(this.__authenticatorSecretKey);
      store.dispatch(openSuccess('Secret key copied to clipboard'));
    } catch (error) {
      store.dispatch(openError('Failed to copy secret key'));
    }
  }

  __renderInstructionalText() {
    return html`
      <div id="${ELEMENTS.info.id}" class="info">
        Configure security settings for this user account.
      </div>
    `;
  }

  __renderVerifiedIcon() {
    return html`
      <neb-icon
        id="${ELEMENTS.greenShieldCheckmarkIcon.id}"
        icon="neb:greenShieldCheckmark"
        style="height: 24px; width: 24px;"
      ></neb-icon>
      <span class="verified-text">Verified!</span>
    `;
  }

  __renderAuthenticatorOnlyMethod() {
    const isVerified =
      this.model?.twoStepVerificationEnabled &&
      this.model?.twoStepMethod === 'authenticator';

    return html`
      <div class="method-selection">
        <div class="method-label">
          Follow the steps below to receive verification code:
        </div>
        <div class="radio-options radio-options--auth-only">
          <div class="radio-group radio-group--auth-only">
            <neb-radio-button
              id="${ELEMENTS.twoStepMethodAuthenticator.id}"
              name="twoStepMethod"
              label="Authenticator App"
              value="authenticator"
              ?checked="${this.model?.twoStepMethod === 'authenticator'}"
              .onChange="${this.__handlers.change}"
            ></neb-radio-button>
            ${isVerified
              ? html`
                  <div></div>
                  <div></div>
                  ${this.__renderVerifiedIcon()}
                `
              : ''}
          </div>
        </div>
      </div>
    `;
  }

  __renderPhoneMethodGroup() {
    const isPhoneVerified =
      this.model?.twoStepVerificationEnabled &&
      this.model?.twoStepMethod === 'phone';
    const isPhoneDisabled =
      this.model?.twoStepVerificationEnabled &&
      this.model?.twoStepMethod === 'authenticator';

    return html`
      <div class="radio-group">
        <neb-radio-button
          id="${ELEMENTS.twoStepMethodPhone.id}"
          name="twoStepMethod"
          label="Phone"
          value="phone"
          ?checked="${this.model?.twoStepMethod === 'phone'}"
          ?disabled="${isPhoneDisabled}"
          .onChange="${this.__handlers.change}"
        ></neb-radio-button>
        ${isPhoneVerified
          ? this.__renderVerifiedIcon()
          : html`<span></span><span></span>`}
      </div>
    `;
  }

  __renderAuthenticatorMethodGroup() {
    const isAuthVerified =
      this.model?.twoStepVerificationEnabled &&
      this.model?.twoStepMethod === 'authenticator';
    const isAuthDisabled =
      this.model?.twoStepVerificationEnabled &&
      this.model?.twoStepMethod === 'phone';

    return html`
      <div class="radio-group">
        <neb-radio-button
          id="${ELEMENTS.twoStepMethodAuthenticator.id}"
          name="twoStepMethod"
          label="Authenticator App"
          value="authenticator"
          ?checked="${this.model?.twoStepMethod === 'authenticator'}"
          ?disabled="${isAuthDisabled}"
          .onChange="${this.__handlers.change}"
        ></neb-radio-button>
        ${isAuthVerified ? this.__renderVerifiedIcon() : ''}
      </div>
    `;
  }

  __renderBothMethodsSelection() {
    return html`
      <div class="method-selection">
        <div class="method-label">
          Pick how you want to receive verification code:
        </div>
        <div class="radio-options">
          ${this.__renderPhoneMethodGroup()}
          ${this.__hasOwlHide2faSmsFeatureFlag ? html`<div></div>` : ''}
          ${this.__renderAuthenticatorMethodGroup()}
          ${this.__hasOwlHide2faSmsFeatureFlag
            ? html`<div></div>
                <div></div>`
            : ''}
        </div>
      </div>
    `;
  }

  __renderMethodSelection() {
    if (this.__hasOwlHide2faSmsFeatureFlag) {
      return this.__renderAuthenticatorOnlyMethod();
    }
    return this.__renderBothMethodsSelection();
  }

  __renderPhoneSetup() {
    if (!this.__isSettingUp2FA || this.model.twoStepVerificationEnabled) {
      return '';
    }

    return html`
      <div class="setup-step">
        <div class="step-heading">Step 1: Enter your phone number</div>
        <div class="step-description">
          By entering your phone number, you agree to receive a unique
          verification code on your mobile device.
        </div>
        <div class="phone-input-container">
          <neb-textfield
            id="${ELEMENTS.phoneNumberInput.id}"
            class="field"
            maxLength="14"
            name="twoStepPhoneNumber"
            label="Phone Number"
            .inputMode="${'numeric'}"
            .mask="${phone}"
            .value="${this.model.twoStepPhoneNumber}"
            .error="${this.errors.twoStepPhoneNumber}"
            .onChange="${this.__handlers.change}"
            @keydown="${e => {
              if (e.key === 'Enter') {
                if (
                  !this.__sendCodeDisabled &&
                  this.__getPhoneDigitsCount() >= 10
                ) {
                  this.__handlers.sendCode();
                }
              }
            }}"
          ></neb-textfield>
          <neb-button
            id="${ELEMENTS.sendCodeButton.id}"
            class="send-code-button"
            role="${BUTTON_ROLE.CONFIRM}"
            label="${this.__getButtonLabel()}"
            ?preserve-case="${true}"
            ?disabled="${this.__sendCodeDisabled ||
            this.__getPhoneDigitsCount() < 10}"
            .onClick="${this.__handlers.sendCode}"
          ></neb-button>
        </div>
      </div>
    `;
  }

  __renderVerificationStep() {
    if (this.model.twoStepVerificationEnabled) {
      return '';
    }

    return html`
      <div class="setup-step">
        <div class="step-heading">Step 2: Enter the code</div>
        <div class="step-description">
          Enter the code to complete 2-step verification
        </div>
        <neb-two-factor-auth
          .onChange="${code => this.__handleVerificationCodeChange(code)}"
          .autoSubmitOnComplete="${true}"
          .onResend="${() => {
            this.__handlers.sendCode();
          }}"
        ></neb-two-factor-auth>
      </div>
    `;
  }

  __renderAuthenticatorSetup() {
    if (!this.__isSettingUp2FA || this.model.twoStepVerificationEnabled) {
      return '';
    }

    return html`
      <div class="step-heading">Step 1: Download an authenticator app</div>
      <p class="step-description">
        Download an authenticator app like Google Authenticator or Microsoft
        Authenticator
      </p>

      <div class="step-heading">Step 2: Scan QR code</div>
      <p class="step-description">
        Scan this QR code with your authenticator app
      </p>

      <div class="qr-code-container">
        <div class="qr-code">
          <img
            src=${this.__authenticatorQrCodeUrl}
            alt="QR Code for Authenticator Setup"
            width="200"
            height="200"
          />
        </div>
        <span class="secret-key-link" @click="${this.__toggleSecretKey}"
          >Can't scan the QR code? Click here to view code</span
        >
        ${this.__showSecretKey
          ? html`
              <div class="secret-key-container">
                <code class="secret-key">${this.__authenticatorSecretKey}</code>
                <button
                  class="copy-button"
                  @click="${this.__copySecretKey}"
                  title="Copy secret key"
                >
                  <neb-icon icon="neb:copy"></neb-icon>
                </button>
              </div>
            `
          : ''}
      </div>

      <div class="step-heading">Step 3: Enter verification code</div>
      <p class="step-description">
        Enter the 6-digit code from your authenticator app
      </p>

      <div class="verification-container">
        <neb-two-factor-auth
          .onChange="${code => this.__verifyAuthenticatorCode(code)}"
          .authenticator="${true}"
          ?disabled="${this.__authenticatorSetupComplete}"
        ></neb-two-factor-auth>
      </div>
    `;
  }

  __renderCelebrationMessage() {
    return html`
      <div class="celebration-message">
        ${this.__renderCelebrationIcons()} ${this.__renderCelebrationTitle()}
        ${this.__renderMethodSpecificMessage()}
        ${this.__renderReminderMessage()}
      </div>
    `;
  }

  __renderCelebrationIcons() {
    return html`
      <div class="celebration-icons">
        <neb-icon
          class="celebration-icon"
          icon="neb:greenShieldCheckmark"
        ></neb-icon>
      </div>
    `;
  }

  __renderCelebrationTitle() {
    return html`
      <div class="celebration-title">
        🎉 2-Step Verification Setup Successful! 🎉
      </div>
    `;
  }

  __renderMethodSpecificMessage() {
    if (this.model.twoStepMethod === 'phone') {
      const phoneDigits = this.__userEnteredPhoneNumber.replace(/\D/g, '');
      const lastFourDigits = phoneDigits.slice(-4);
      const phoneFormat = `(***) *** - ${lastFourDigits}`;

      return html`
        <div>
          ${this.layout === 'small'
            ? html`Congratulations! Your account associated with phone number
                ${phoneFormat} is now<br />protected with an extra layer of
                security.`
            : html`Congratulations! Your account associated with phone number<br />${phoneFormat}
                is now protected with an extra layer of security.`}
        </div>
      `;
    }

    return html`
      <div>
        Congratulations! Your account is now protected with an extra layer of
        security using an authenticator app.
      </div>
    `;
  }

  __renderReminderMessage() {
    return html`
      <div style="margin-top: 14px; font-size: 14px; opacity: 0.9;">
        Remember: You'll need both your password and a verification code to sign
        in.
      </div>
    `;
  }

  async __setupAuthenticator() {
    try {
      const secretKey = await setupTOTP(this.__email, this.__refreshToken);
      this.__authenticatorSecretKey = secretKey;
      this.__authenticatorQrCodeUrl = await generateTOTPQRCode(
        this.__email,
        secretKey,
      );

      this.requestUpdate();
    } catch (error) {
      store.dispatch(
        openError(`Error setting up authenticator app: ${error.message}`),
      );
    }
  }

  async __verifyAuthenticatorCode(code) {
    if (code.length !== 6) {
      return;
    }

    try {
      const message = await verifyTOTP(this.__email, code, this.__refreshToken);
      this.__authenticatorSetupComplete = true;

      this.onChange({
        name: 'twoStepVerificationEnabled',
        value: true,
        skipDismiss: true,
      });

      this.onChange({
        name: 'twoStepMethod',
        value: 'authenticator',
        skipDismiss: true,
      });

      const cognitoId = store.getState().session?.item?.id;
      const response = await updateTwoFactorAuth(cognitoId, {
        twoStepVerificationEnabled: true,
        twoStepMethod: this.model.twoStepMethod,
        twoStepPhoneNumber: '',
      });

      this.model = {
        ...this.model,
        ...response.data,
        verified: true,
        twoStepPhoneNumber: '',
        skipDismiss: true,
      };

      store.dispatch(openSuccess(message));

      const event = new CustomEvent('security-update', {
        detail: {
          ...this.model,
          skipDismiss: true,
        },
        bubbles: true,
        composed: true,
      });
      this.dispatchEvent(event);

      this.__updateParentFormModel();

      const saveEvent = new CustomEvent('security-save', {
        detail: {
          ...this.model,
          skipDismiss: true,
        },
        bubbles: true,
        composed: true,
      });
      this.dispatchEvent(saveEvent);

      this.__triggerConfetti();
      await this.onSave({
        ...this.model,
        skipDismiss: true,
      });

      this.onChangeDirty(false);
    } catch (error) {
      store.dispatch(openError(error.message));

      const verificationComponent = this.shadowRoot.querySelector(
        'neb-two-factor-auth',
      );

      if (verificationComponent) {
        verificationComponent.__error = true;
      }
      throw error;
    }
  }

  async __handleTwoStepSetupChange(enableToggleState, cognitoId) {
    if (enableToggleState) {
      await this.__handleEnableTwoStepSetup();
    } else {
      await this.__handleDisableTwoStepSetup(cognitoId);
    }
  }

  async __handleEnableTwoStepSetup() {
    this.__startTwoStepSetup();

    if (this.__hasOwlHide2faSmsFeatureFlag) {
      await this.__setupAuthenticatorForFeatureFlag();
    }
  }

  async __setupAuthenticatorForFeatureFlag() {
    await this.updateComplete;
    const authenticatorRadio = this.shadowRoot.querySelector(
      `#${ELEMENTS.twoStepMethodAuthenticator.id}`,
    );

    if (authenticatorRadio) {
      authenticatorRadio.checked = true;

      this.model = {
        ...this.model,
        twoStepMethod: 'authenticator',
      };

      this.onChange({
        name: 'twoStepMethod',
        value: 'authenticator',
      });

      if (!this.__authenticatorSecretKey) {
        this.__setupAuthenticator();
      }
    }
  }

  async __handleDisableTwoStepSetup(cognitoId) {
    if (this.model.twoStepVerificationEnabled) {
      await this.__handleDisableTwoStep(cognitoId);
    } else if (this.__isSettingUp2FA) {
      this.__cancelTwoStepSetup();
    } else {
      this.onChangeDirty(true);
    }
  }

  __startTwoStepSetup() {
    this.__isSettingUp2FA = true;
    this.__setupStep = 1;
    this.__sendCodeDisabled = true;

    this.onChangeDirty(true);
  }

  __cancelTwoStepSetup() {
    this.__resetTwoStepSetup();
    this.onChangeDirty(false);
    this.requestUpdate();
  }

  async __handleDisableTwoStep(cognitoId) {
    this.__isSettingUp2FA = false;

    this.onChangeDirty(false);

    const confirmed = await openPopup(
      POPUP_RENDER_KEYS.CONFIRM,
      CONFIRM_DISABLE_2FA,
    );

    if (confirmed) {
      await this.__disableTwoStepVerification(cognitoId);
    } else {
      this.onChangeDirty(false);
    }
  }

  __resetTwoStepSetup() {
    this.__setupStep = 1;
    this.__sendCodeDisabled = true;
    this.__isSettingUp2FA = false;
  }

  async __disableTwoStepVerification(cognitoId) {
    try {
      await disableMfa(this.__email, this.__refreshToken);
      const payload = this.__getDisabledTwoFactorState();
      const response = await updateTwoFactorAuth(cognitoId, payload);

      const responseData = this.__validateUpdateTwoFactorAuthResponse(response);

      this.__resetTwoStepSetup();
      this.__setModelDisabled(responseData);

      const event = new CustomEvent('security-update', {
        detail: {
          ...this.model,
          skipDismiss: true,
        },
        bubbles: true,
        composed: true,
      });
      this.dispatchEvent(event);

      this.__updateParentFormModel();

      const modelWithSkip = { ...this.model, skipDismiss: true };
      this.__dispatchSecuritySaveEvent(modelWithSkip);

      this.__showDisableSuccess();
      const result = await this.onSave(modelWithSkip);

      this.onChangeDirty(false);
      return result;
    } catch (error) {
      this.__handleDisable2FAError(error);
      throw error;
    }
  }

  __handleTwoFactorError(message) {
    this.__isSettingUp2FA = false;
    throw new Error(
      `Failed to disable two-factor authentication: ${message}. Please try again.`,
    );
  }

  __getDisabledTwoFactorState() {
    return {
      twoStepVerificationEnabled: false,
      twoStepMethod: 'phone',
      twoStepPhoneNumber: '',
    };
  }

  __isEmptyResponse(response) {
    return !response.data || Object.keys(response.data).length === 0;
  }

  __validateUpdateTwoFactorAuthResponse(response) {
    if (!response) {
      this.__handleTwoFactorError(
        'No response received from updateTwoFactorAuth',
      );
    }

    if (response.status === 200 && this.__isEmptyResponse(response)) {
      return this.__getDisabledTwoFactorState();
    }

    if (this.__isEmptyResponse(response)) {
      this.__handleTwoFactorError(
        'No response received from updateTwoFactorAuth',
      );
    }

    return response.data || response;
  }

  __setModelEnabled() {
    this.model = { ...this.model, twoStepVerificationEnabled: true };
  }

  __setModelDisabled(responseData) {
    this.model = {
      ...this.model,
      ...responseData,
      twoStepVerificationEnabled: false,
      twoStepPhoneNumber: '',
      twoStepMethod: 'phone',
      skipDismiss: true,
    };
  }

  __dispatchSecuritySaveEvent(modelWithSkip) {
    const saveEvent = new CustomEvent('security-save', {
      detail: modelWithSkip,
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(saveEvent);
  }

  __showDisableSuccess() {
    store.dispatch(
      openSuccess('Two-factor authentication disabled successfully'),
    );
  }

  __handleDisable2FAError(error) {
    if (
      error.message.includes('No response received') ||
      error.message.includes('No data in')
    ) {
      this.__setModelEnabled();
      this.__isSettingUp2FA = false;
    }
    const errorMessage = error.response?.data?.message || error.message;
    store.dispatch(
      openError(
        `Failed to disable two-factor authentication: ${errorMessage}. Please try again.`,
      ),
    );
  }

  __handlePhoneNumberChange(event) {
    this.__userEnteredPhoneNumber = event.value;
    this.model = {
      ...this.model,
      [event.name]: event.value,
    };

    const digitsOnly = event.value.replace(/\D/g, '');
    this.__sendCodeDisabled = digitsOnly.length !== 10;
    this.requestUpdate();
  }

  __handleModelChange(event) {
    this.model = {
      ...this.model,
      [event.name]: event.value,
    };

    if (
      event.name === 'twoStepMethod' &&
      event.value === 'authenticator' &&
      !this.__authenticatorSecretKey
    ) {
      this.__setupAuthenticator();
    }
  }

  updated(changedProps) {
    super.updated(changedProps);

    if (changedProps.has('model')) {
      if (
        this.model.twoStepPhoneNumber &&
        this.model.twoStepPhoneNumber.startsWith('+1')
      ) {
        const rawNumber = this.model.twoStepPhoneNumber.substring(2);
        this.model = {
          ...this.model,
          twoStepPhoneNumber: phone.toFormattedValue(rawNumber),
        };
      }
    }
  }

  __renderExpandedContent() {
    return html`
      <div slot="expanded-content">
        ${this.__renderMethodSelection()}
        ${this.model.twoStepMethod === 'phone' &&
        !this.model.twoStepVerificationEnabled
          ? html`
              ${this.__renderPhoneSetup()}
              ${this.__setupStep === 2 ? this.__renderVerificationStep() : ''}
            `
          : html`
              ${this.model.twoStepMethod === 'authenticator'
                ? this.__renderAuthenticatorSetup()
                : ''}
            `}
      </div>
    `;
  }

  __getExpandedContent() {
    if (this.__isSettingUp2FA || this.model.twoStepVerificationEnabled) {
      return html`${this.__renderExpandedContent()}`;
    }
    return '';
  }

  __renderCardSwitchExpanded() {
    return html`
      <neb-card-switch-expanded
        id="${ELEMENTS.twoStepVerification.id}"
        class="permission"
        name="showTwoStepSetup"
        description="Add another layer of security by requiring both your password and a verification code upon sign in."
        title="2-Step Verification"
        icon="neb:security"
        .active="${this.model.twoStepVerificationEnabled ||
        this.__isSettingUp2FA}"
        .layout="${this.layout}"
        .onChange="${this.__handlers.change}"
      >
        ${this.__getExpandedContent()}
      </neb-card-switch-expanded>
      ${this.model.twoStepVerificationEnabled
        ? this.__renderCelebrationMessage()
        : ''}
    `;
  }

  __getPhoneDigitsCount() {
    return (this.model.twoStepPhoneNumber || '').replace(/\D/g, '').length;
  }

  __getButtonLabel() {
    if (this.__sendCodeDisabled && this.__getPhoneDigitsCount() === 10) {
      return 'Code Sent';
    }
    return 'Send Code';
  }

  render() {
    return html`
      <div id="${ELEMENTS.container.id}" class="container">
        ${this.__renderInstructionalText()}
        <div id="${ELEMENTS.containerSecurity.id}" class="container-security">
          ${this.__renderCardSwitchExpanded()}
        </div>
      </div>
    `;
  }
}

customElements.define('neb-security', NebSecurity);
